/**
 * The one gtag transport.
 *
 * Extracted from useOnboardingEvents.js when a second event family (the upsell
 * funnel, useUpsellEvents.js) arrived. That file's header already promised
 * "swapping transports is a one-file change"; copying safeGtag/emit into the
 * new module would have quietly made it a two-file change, so the promise
 * moved here and both event modules consume it.
 *
 * Named `analyticsTransport` rather than `useAnalytics` on purpose: it is not
 * a composable. It holds no reactive state, takes no component instance, and
 * is safe to call from anywhere — including outside setup().
 *
 * gtag is installed unconditionally at boot (main.js) with no consent/CMP
 * gating anywhere in the repo, so callers carry no consent obligation here.
 * If that ever changes, this is the single place the gate belongs.
 */

/**
 * The live gtag function, or null when analytics is not on the page — SSR/unit
 * environments with no window, a build where the snippet did not load, or a
 * browser extension that stripped it.
 * @returns {Function|null}
 */
function safeGtag() {
  if (typeof window === "undefined") return null;
  if (typeof window.gtag === "function") return window.gtag;
  return null;
}

/**
 * Fire one gtag event. Never throws: analytics is observation, and an ad
 * blocker replacing gtag with something that throws must not be able to break
 * the flow the caller is instrumenting.
 * @param {string} eventName
 * @param {Object} [params]
 */
export function emit(eventName, params) {
  const gtag = safeGtag();
  if (gtag) {
    try {
      gtag("event", eventName, params || {});
    } catch (err) {
      console.warn(`[analytics] failed to emit ${eventName}:`, err);
    }
  } else if (process.env.NODE_ENV !== "production") {
    console.debug(`[analytics] ${eventName}`, params);
  }
}
